import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Loader from '../../../components/Loader.js';
import { updateTitle } from '../../../helpers/titleHelper.js';
import { getAllPeopleLogs, exportPeopleLogs } from '../../../api/districtuserapi.js';
import { formatDateTimeForUserTimeZone } from '../../../helpers/DateTimeFormatter.js';

export default function PeopleLoginLogs() {
    updateTitle('Admin', 'People Login logs');

    const [loadingData, setLoadingData] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [total, setTotal] = useState(0);
    const [peopleLogs, setPeopleLogs] = useState([]);
    const [filter, setFilter] = useState({ start: 0, length: 100, sort: 1 });

    const loadMoreRef = useRef(null);

    const fetchPeopleLogs = async (append = false) => {
        if (append) setLoadingMore(true);
        else setLoadingData(true);

        try {
            const response = await getAllPeopleLogs(filter);
            const data = response.data.data.data || [];
            const totalRecords = response.data.data.totalRecords || 0;

            setTotal(totalRecords);
            setPeopleLogs(prev => append ? [...prev, ...data] : data);
        } catch (err) {
            console.error('Error fetching people login logs:', err);
        } finally {
            if (append) setLoadingMore(false);
            else setLoadingData(false);
        }
    };

    const loadMoreLogs = () => {
        if (loadingMore || peopleLogs.length >= total) return;
        const newStart = filter.start + filter.length;
        setFilter(prev => ({ ...prev, start: newStart }));
    };

    useEffect(() => {
        fetchPeopleLogs(filter.start > 0);
    }, [filter]);

    useEffect(() => {
        if (!loadMoreRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMoreLogs();
                }
            },
            { threshold: 1 }
        );

        observer.observe(loadMoreRef.current);
        return () => observer.disconnect();
    }, [peopleLogs, total, loadingMore]);

    const handleExport = async () => {
        try { 
            const response = await exportPeopleLogs();
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${'PeopleLoginLogs'}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error downloading the file", error);
        }
    };

    return (
        <div className="admin-page-wrap">
            <div className="admin-top-bar-wrap">
                <div className="admin-top-bar-left">
                    <Link className="admin-top-bar-back" to="/admin/">Back</Link>
                </div>
            </div>

            <div className="admin-page-content">
                <h1 className="admin-page-heading">People Login Logs</h1>
                <button  className="admin-button power" onClick={handleExport} > Export Login Logs </button>
                {loadingData ? (
                    <Loader />
                ) : (
                    <>
                        <div className="admin-bottom-bar"></div>

                        <div className="admin-table-wrap">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th onClick={() => setFilter({ ...filter, start: 0, sort: filter.sort == 2 ? 3 : 2 })} className={filter.sort == 2 ? 'admin-table-name-cell sortable sorted-dn' : (filter.sort == 3 ? 'admin-table-name-cell sortable sorted-up' : 'admin-table-name-cell sortable')}>User Name</th>
                                        <th onClick={() => setFilter({ ...filter, start: 0, sort: filter.sort == 4 ? 5 : 4 })} className={filter.sort == 4 ? 'admin-table-name-cell sortable sorted-dn' : (filter.sort == 5 ? 'admin-table-name-cell sortable sorted-up' : 'admin-table-name-cell sortable')}>Email</th>
                                        <th onClick={() => setFilter({ ...filter, start: 0, sort: filter.sort == 0 ? 1 : 0 })} className={filter.sort == 0 ? 'admin-table-name-cell sortable sorted-dn' : (filter.sort == 1 ? 'admin-table-name-cell sortable sorted-up' : 'admin-table-name-cell sortable')}>Created Date</th>
                                        <th onClick={() => setFilter({ ...filter, start: 0, sort: filter.sort == 6 ? 7 : 6 })} className={filter.sort == 6 ? 'admin-table-name-cell sortable sorted-dn' : (filter.sort == 7 ? 'admin-table-name-cell sortable sorted-up' : 'admin-table-name-cell sortable')}>IPAddress</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {peopleLogs.length > 0 ? (
                                        peopleLogs.map((peopleLog, index) => (
                                            <tr key={index}>
                                                <td className="admin-table-name-cell space-nowrap">{peopleLog?.userName}</td>
                                                <td className="admin-table-name-cell space-nowrap">{peopleLog?.email}</td>
                                                <td className="admin-table-name-cell">{formatDateTimeForUserTimeZone(peopleLog?.created)}</td>
                                                <td className="admin-table-name-cell">{peopleLog?.ipAddress}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="admin-table-name-cell">No active people login logs</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Load More Button */}
                        {peopleLogs.length < total && (
                            <div ref={loadMoreRef} style={{ height: '40px', marginTop: '1rem' }}>
                                {loadingMore && <Loader />}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
